/**
 * One part's pricing (#3034): which way its catalog price is kept (`manual`, or `category_margin` from a margin) and an
 * optional margin override that beats the category rule. Changing either never writes a price; the price changes only
 * through the ordinary part edit or an explicit category recalculation. What is shown about the price comes from the
 * server's resolver, and what is shown as the part's current mode and override comes from the server's current-pricing read
 * (`GET /part-pricing/parts/:partId`); the browser calculates nothing and guesses nothing. While that read is loading, failed or
 * answered 404 there is no current state, so no control is offered; a stored mode the API does not know is shown as needing
 * attention and is never presented as `manual`.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useNotify } from '@/stores/notificationStore';
import {
  usePartPriceResolution,
  usePartPricing,
  usePricingPermissions,
  useUpdatePartPricingMode,
  type CurrentPartPricing,
  type PartPriceMode,
  type UpdatePartPricingBody,
} from '@/hooks/usePartPricing';
import { formatDate, formatEuros, formatPercent, impliedMarkupOfMargin, percentInput } from './decimal';
import { PricingErrorAlert } from './feedback';
import { OwnedBoundary } from './OwnedBoundary';
import { isSessionCancel } from './pricingSession';
import { PartPicker, type PickedPart } from './pickers';

type OverrideAction = 'keep' | 'set' | 'clear';

interface FormProps {
  part: PickedPart;
  /** The part's pricing as the server has it right now; it follows every refetch, the drafts below do not. */
  current: CurrentPartPricing;
}

/**
 * Keyed by part: its drafts belong to one part. The current server state arrives as a prop and may change under the drafts
 * (a refetch, a save readback); nothing here copies it into state, so a refetch never overwrites what was typed.
 */
function PartPricingForm({ part, current }: FormProps) {
  const { t, i18n } = useTranslation('partPricing');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const { canManage } = usePricingPermissions();
  const update = useUpdatePartPricingMode();
  const [modeDraft, setModeDraft] = useState<PartPriceMode | null>(null);
  const [overrideAction, setOverrideAction] = useState<OverrideAction>('keep');
  const [overrideText, setOverrideText] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const stored = current.priceMode;
  // An unrecognised stored mode selects nothing: the user must choose one on purpose.
  const mode = modeDraft ?? (stored === 'unknown' ? null : stored);
  const override = percentInput(overrideText, 'margin');
  const overrideInvalid = overrideAction === 'set' && !override.ok;
  const modeChanged = modeDraft !== null && modeDraft !== stored;
  const changed = modeChanged || overrideAction !== 'keep';
  const busy = update.isPending;
  const markup = overrideAction === 'set' && override.ok ? impliedMarkupOfMargin(override.value, i18n.language) : null;

  const save = async () => {
    setShowErrors(true);
    if (!changed || overrideInvalid) return;
    const body: UpdatePartPricingBody = {};
    if (modeChanged && modeDraft) body.priceMode = modeDraft;
    if (overrideAction === 'set' && override.ok) body.marginOverridePercent = override.value;
    if (overrideAction === 'clear') body.marginOverridePercent = null;
    setError(null);
    try {
      await update.mutateAsync({ partId: part.id, body });
      notify.success(t('parts.saved', { part: part.name }));
      setModeDraft(null);
      setOverrideAction('keep');
      setOverrideText('');
      setShowErrors(false);
    } catch (failure) {
      if (isSessionCancel(failure)) return;
      setError(failure);
    }
  };

  if (!canManage) return <Alert severity="info">{t('parts.readOnly')}</Alert>;

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1">{t('parts.controlTitle')}</Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={mode}
        disabled={busy}
        aria-label={t('fields.priceMode')}
        onChange={(_event, next: PartPriceMode | null) => {
          // Only a choice that differs from what the server holds is a draft: it is then a change the user made, not a
          // copy of a state that another session may since have replaced.
          if (next) setModeDraft(next === stored ? null : next);
        }}
      >
        <ToggleButton value="manual">{t('priceMode.manual')}</ToggleButton>
        <ToggleButton value="category_margin">{t('priceMode.category_margin')}</ToggleButton>
      </ToggleButtonGroup>
      <Typography variant="caption" color="text.secondary">
        {t('hints.modeNoPriceChange')}
      </Typography>

      <Box>
        <Typography variant="subtitle2">{t('parts.overrideTitle')}</Typography>
        <CurrentOverrideText current={current} />
      </Box>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={overrideAction}
        disabled={busy}
        aria-label={t('parts.overrideTitle')}
        onChange={(_event, next: OverrideAction | null) => {
          if (next) setOverrideAction(next);
        }}
      >
        <ToggleButton value="keep">{t('parts.overrideKeep')}</ToggleButton>
        <ToggleButton value="set">{t('parts.overrideSet')}</ToggleButton>
        <ToggleButton value="clear">{t('parts.overrideClear')}</ToggleButton>
      </ToggleButtonGroup>
      {overrideAction === 'set' && (
        <TextField
          size="small"
          required
          label={t('fields.marginOverride')}
          value={overrideText}
          disabled={busy}
          onChange={(event) => setOverrideText(event.target.value)}
          error={showErrors && !override.ok}
          helperText={showErrors && !override.ok ? t(`validation.margin.${override.error}`) : t('hints.marginDefinition')}
          slotProps={{
            htmlInput: { inputMode: 'decimal' },
            input: { endAdornment: <InputAdornment position="end">{t('units.percent')}</InputAdornment> },
          }}
        />
      )}
      {markup && (
        <Typography variant="body2" color="text.secondary">
          {t('hints.impliedMarkup', { markup })}
        </Typography>
      )}

      <PricingErrorAlert error={error} />
      <Box>
        <Button variant="contained" disabled={busy || !changed} onClick={() => void save()}>
          {busy ? tc('saving') : tc('save')}
        </Button>
      </Box>
    </Stack>
  );
}

/** The override the server holds: none (`null`), or its exact value, a real zero included. */
function CurrentOverrideText({ current }: { current: CurrentPartPricing }) {
  const { t, i18n } = useTranslation('partPricing');
  return (
    <Typography variant="body2" color="text.secondary">
      {current.marginOverridePercent === null
        ? t('parts.overrideNone')
        : t('parts.overrideCurrent', {
            margin: formatPercent(current.marginOverridePercent, i18n.language) || current.marginOverridePercent,
          })}
    </Typography>
  );
}

/** What the server holds for the part now: the mode (an unknown one needs attention) and, for a reader, the override. */
function CurrentPricingSummary({ current, showOverride }: { current: CurrentPartPricing; showOverride: boolean }) {
  const { t } = useTranslation('partPricing');
  const { canManage } = usePricingPermissions();
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2">
        {t('parts.currentMode')}: {t(`priceMode.${current.priceMode}`)}
      </Typography>
      {current.priceMode === 'unknown' && (
        <Alert severity="warning">{canManage ? t('parts.modeUnknownManage') : t('parts.modeUnknown')}</Alert>
      )}
      {showOverride && <CurrentOverrideText current={current} />}
    </Stack>
  );
}

function PartPricingPanelBody() {
  const { t, i18n } = useTranslation('partPricing');
  const { t: tc } = useTranslation('common');
  const { canManage } = usePricingPermissions();
  const [part, setPart] = useState<PickedPart | null>(null);
  const resolution = usePartPriceResolution(part ? { partIds: [part.id] } : null);
  const current = usePartPricing(part?.id ?? null);
  const price = resolution.data?.prices[0];
  const currentStatus = (current.error as { status?: unknown } | null)?.status;

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6">{t('parts.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('parts.intro')}
        </Typography>
      </Box>
      <PartPicker label={t('fields.part')} value={part} onChange={setPart} />

      {part && (
        <Stack spacing={2}>
          {resolution.isLoading && <Typography color="text.secondary">{tc('loading')}</Typography>}
          {resolution.isError && <PricingErrorAlert error={resolution.error} />}
          {price?.status === 'resolved' && (
            <Stack spacing={0.5}>
              <Typography variant="subtitle1">{t('parts.priceTitle')}</Typography>
              <Typography variant="body2">
                {t('parts.catalogPrice')}: {formatEuros(price.baseNetCents, i18n.language)}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">
                  {t('parts.priceToday')}: {formatEuros(price.unitNetCents, i18n.language)}
                </Typography>
                <Chip size="small" label={t(`source.${price.source}`)} />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {t('parts.netNote', { date: formatDate(resolution.data?.asOf ?? '', i18n.language) })}
              </Typography>
            </Stack>
          )}
          {price?.status === 'unavailable' && <Alert severity="warning">{t('parts.priceUnavailable')}</Alert>}
          {price?.status === 'part_not_found' && <Alert severity="error">{t('parts.notFound')}</Alert>}

          {current.isPending && current.fetchStatus !== 'idle' && <Typography color="text.secondary">{t('parts.loadingCurrent')}</Typography>}
          {current.data === undefined && current.isError && currentStatus === 404 && price?.status !== 'part_not_found' && (
            <Alert severity="error">{t('parts.notFound')}</Alert>
          )}
          {current.data === undefined && current.isError && currentStatus !== 404 && (
            <Stack spacing={1} alignItems="flex-start">
              <Alert severity="error" role="alert">{t('parts.currentReadFailed')}</Alert>
              <PricingErrorAlert error={current.error} />
              <Button size="small" variant="outlined" onClick={() => void current.refetch()}>
                {tc('errors.refresh')}
              </Button>
            </Stack>
          )}
          {current.data !== undefined && (
            <>
              {current.isError && <Alert severity="warning">{t('parts.refreshFailed')}</Alert>}
              <CurrentPricingSummary current={current.data} showOverride={!canManage} />
              <PartPricingForm key={part.id} part={part} current={current.data} />
            </>
          )}
        </Stack>
      )}
    </Stack>
  );
}

export function PartPricingPanel() {
  return (
    <OwnedBoundary>
      <PartPricingPanelBody />
    </OwnedBoundary>
  );
}
